// Cookie Consent Management

export interface Consent {
  necessary: true;
  analytics: boolean;
  functional: boolean;
  ts: string;
  expiresAt: string; // Date d'expiration du consentement (6 mois max CNIL)
}

const KEY = 'tp13_cookie_consent_v1';
const CONSENT_DURATION_MONTHS = 6; // Durée max CNIL : 6 mois

export function getConsent(): Consent | null {
  try {
    const stored = localStorage.getItem(KEY);
    if (!stored) return null;
    
    const consent = JSON.parse(stored) as Consent;
    
    // Vérifier si le consentement a expiré (6 mois max CNIL)
    if (consent.expiresAt && new Date(consent.expiresAt) < new Date()) {
      // Consentement expiré, le supprimer
      localStorage.removeItem(KEY);
      return null;
    }
    
    return consent;
  } catch {
    return null;
  }
}

export function setConsent(consent: Omit<Consent, 'expiresAt'>): void {
  try {
    // Calculer la date d'expiration (6 mois)
    const expiresAt = new Date();
    expiresAt.setMonth(expiresAt.getMonth() + CONSENT_DURATION_MONTHS);
    
    const fullConsent: Consent = {
      ...consent,
      expiresAt: expiresAt.toISOString(),
    };
    
    localStorage.setItem(KEY, JSON.stringify(fullConsent));
    // Dispatch event pour notifier les composants
    window.dispatchEvent(new CustomEvent('tp13:consent', { detail: fullConsent }));
  } catch (err) {
    console.error('Failed to save consent:', err);
  }
}

export function hasConsent(category: 'necessary' | 'analytics' | 'functional'): boolean {
  const consent = getConsent();
  if (!consent) return false;
  return consent[category] === true;
}

export function acceptAll(): void {
  setConsent({
    necessary: true,
    analytics: true,
    functional: true,
    ts: new Date().toISOString(),
  });
}

export function rejectAll(): void {
  setConsent({
    necessary: true,
    analytics: false,
    functional: false,
    ts: new Date().toISOString(),
  });
}

export function clearConsent(): void {
  try {
    localStorage.removeItem(KEY);
  } catch (err) {
    console.error('Failed to clear consent:', err);
  }
}